import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MessageSquare, X, History, Loader2 } from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { JobService } from "@/services/job.service";
import { JobChatThread } from "@/components/chat/JobChatThread";
import { JobChatTabs } from "@/components/chat/JobChatTabs";
import { ConversationHistoryModal } from "@/components/chat/ConversationHistoryModal";
import type { MessageCategory } from "@/data/jobMessageTypes";
import type { Job } from "@/data/types";
import { detectJobIdFromRoute } from "@/data/frictionLogTypes";

export function GlobalMessengerButton() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<MessageCategory>("orders");
  const [historyOpen, setHistoryOpen] = useState(false);
  const location = useLocation();
  const { jobs } = useAppData();
  const { profile } = useCurrentUser();
  const popupRef = useRef<HTMLDivElement>(null);

  const jobId = detectJobIdFromRoute(location.pathname);
  const selectedJob = jobId ? jobs.find((j) => j.id === jobId) : undefined;

  // When the job isn't in the global jobs array (e.g. server-side query mode
  // on the job detail page where global hydration is deferred), fetch it
  // independently so the messenger button remains visible.
  const { data: fetchedJob } = useQuery({
    queryKey: ["job-detail-for-messenger", jobId ?? ""] as const,
    queryFn: () => JobService.getById(jobId!).then((res) => res.data ?? null),
    enabled: !!jobId && !selectedJob,
    staleTime: 60_000,
  });

  const resolvedJob = selectedJob ?? fetchedJob ?? undefined;

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  if (!jobId) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-16 right-5 z-50 flex items-center gap-1.5 px-3 py-2.5 rounded-full
                   bg-primary text-primary-foreground shadow-lg hover:shadow-xl
                   transition-all"
        title="Messages"
      >
        <MessageSquare className="h-4 w-4" />
      </button>

      {open && (
        <div
          ref={popupRef}
          className="fixed bottom-24 right-5 z-50 w-[360px] h-[500px]
                     bg-background border border-border rounded-xl shadow-2xl
                     flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-primary" />
              <span className="font-semibold text-sm">Messages</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setHistoryOpen(true)}
                className="p-1 hover:bg-muted rounded-md transition-colors"
                title="View conversation history"
                aria-label="View conversation history"
              >
                <History className="h-4 w-4" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1 hover:bg-muted rounded-md transition-colors"
                aria-label="Close messages"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 min-h-0 flex flex-col">
            {!resolvedJob ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="shrink-0 px-3 pt-2">
                  <JobChatTabs
                    value={category}
                    onChange={setCategory}
                    categories={["orders", "concerns"]}
                  />
                </div>
                <div className="flex-1 min-h-0">
                  <JobChatThread
                    key={`${resolvedJob.id}-${category}`}
                    jobId={resolvedJob.id}
                    category={category}
                    currentUserId={profile?.id ?? ""}
                    jobNumber={resolvedJob.jobNumber ?? resolvedJob.id}
                    job={resolvedJob}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {resolvedJob && (
        <ConversationHistoryModal
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          jobId={resolvedJob.id}
          jobNumber={resolvedJob.jobNumber ?? resolvedJob.id}
        />
      )}
    </>
  );
}
